/**
 * Blinkit-theme stores list — the "Shops" tab (``/food``) and a vertical's sellers (``/c/:slug``).
 *   /food     → ``GET /feed`` (nearby + featured shops, sorted from the saved default address), with vertical chips
 *   /c/:slug  → ``GET /category/{slug}`` (that vertical's sellers) plus a shortcut to its products
 * Same endpoints the classic pages call; nothing is filtered on the server's behalf except the chip selection.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search, Store } from "lucide-react";
import { api } from "../../../lib/api";
import { useVerticals } from "../../../lib/catalogData";
import BkStoreCard from "../../../components/blinkit/BkStoreCard";
import { BkChip, BkEmptyState, BkErrorState, BkPageHeader } from "../../../components/blinkit/BkUI";

async function defaultAddressQuery() {
    try {
        const { data } = await api.get("/addresses");
        const def = (data || []).find((a) => a.is_default) || (data || [])[0];
        if (def?.lat != null && def?.lng != null) return `?lat=${def.lat}&lng=${def.lng}`;
    } catch { /* signed out — no distance sort */ }
    return "";
}

export default function BkStoresView({ verticalSlug = null }) {
    const [state, setState] = useState({ data: null, error: false });
    const [attempt, setAttempt] = useState(0);
    const [chip, setChip] = useState(null);
    const verticalsQ = useVerticals();

    useEffect(() => {
        let alive = true;
        setState({ data: null, error: false });
        (async () => {
            const q = await defaultAddressQuery();
            try {
                const { data } = await api.get(verticalSlug ? `/category/${verticalSlug}${q}` : `/feed${q}`);
                if (alive) setState({ data, error: false });
            } catch (e) {
                if (alive) setState({ data: null, error: e?.response?.status === 404 ? "missing" : true });
            }
        })();
        return () => { alive = false; };
    }, [verticalSlug, attempt]);

    const d = state.data;
    const category = verticalSlug ? d?.category : null;
    const stores = useMemo(() => {
        if (!d) return [];
        if (verticalSlug) return d.sellers || [];
        // /feed: nearby covers every visible shop; add featured ones it may not include.
        const seen = new Set();
        return [...(d.nearby_restaurants || []), ...(d.featured_restaurants || [])].filter((r) => (seen.has(r.id) ? false : seen.add(r.id)));
    }, [d, verticalSlug]);

    const parents = (verticalsQ.data || []).filter((v) => !v.parent_id);
    const chipIds = useMemo(() => new Set(stores.flatMap((r) => r.business_category_ids || [])), [stores]);
    const chips = parents.filter((v) => chipIds.has(v.id));
    const shown = chip ? stores.filter((r) => (r.business_category_ids || []).includes(chip)) : stores;
    // A "Featured" shelf only makes sense when it's a subset — if every store is featured, list them once.
    const featuredAll = !verticalSlug && !chip ? shown.filter((r) => r.is_featured) : [];
    const featured = featuredAll.length < shown.length ? featuredAll : [];

    const title = verticalSlug ? (category?.name || "Stores") : "Stores";
    const subtitle = d ? `${shown.length} store${shown.length === 1 ? "" : "s"}` : null;

    return (
        <div className="pb-40 min-w-0" data-testid={verticalSlug ? "category-feed-page" : "bk-stores-page"} data-variant="blinkit">
            <BkPageHeader title={title} subtitle={subtitle} back={verticalSlug ? undefined : "/"}
                          right={<Link to="/search" aria-label="Search" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted"><Search size={20} /></Link>}>
                {chips.length > 1 && (
                    <div className="px-4 pb-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="bk-store-chips">
                        <BkChip active={!chip} onClick={() => setChip(null)} testid="bk-store-chip-all">All</BkChip>
                        {chips.map((v) => <BkChip key={v.id} active={chip === v.id} onClick={() => setChip(v.id)} testid={`bk-store-chip-${v.slug}`}>{v.name}</BkChip>)}
                    </div>
                )}
            </BkPageHeader>

            {state.error === "missing" || (verticalSlug && d && !d.category) ? (
                <BkEmptyState icon={Store} title="This category isn't available" body="Browse every store instead." testid="bk-stores-missing"
                              action={<Link to="/food" className="inline-flex h-10 items-center px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold">See all stores</Link>} />
            ) : state.error ? (
                <BkErrorState onRetry={() => setAttempt((n) => n + 1)} testid="bk-stores-error" />
            ) : !d ? (
                <div className="px-4 pt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3" aria-hidden="true" data-testid="bk-stores-loading">
                    {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-[98px] rounded-2xl bg-card border border-border/80 animate-pulse" />)}
                </div>
            ) : (
                <div className="px-4 pt-4 space-y-6">
                    {verticalSlug && category && (
                        <Link to={`/c/${verticalSlug}/products`} data-testid="bk-stores-products-link"
                              className="flex items-center justify-between gap-3 rounded-2xl bg-primary/[0.07] border border-primary/15 px-4 py-3 active:scale-[0.99] transition">
                            <span className="text-[14px] font-bold text-primary">Shop all {category.name} products</span>
                            <ChevronRight size={18} className="text-primary shrink-0" />
                        </Link>
                    )}
                    {featured.length > 0 && (
                        <section data-testid="bk-stores-featured">
                            <h2 className="text-[17px] font-extrabold tracking-tight mb-3">Featured stores</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                                {featured.map((r) => <BkStoreCard key={r.id} r={r} testid={`bk-store-featured-${r.id}`} />)}
                            </div>
                        </section>
                    )}
                    {shown.length === 0 ? (
                        <BkEmptyState icon={Store} testid="bk-stores-empty" title="No stores here yet"
                                      body="New stores are being added. Meanwhile, browse all products."
                                      action={<Link to="/products" className="inline-flex h-10 items-center px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold">Browse products</Link>} />
                    ) : (
                        <section data-testid="bk-stores-all">
                            {featured.length > 0 && <h2 className="text-[17px] font-extrabold tracking-tight mb-3">All stores</h2>}
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                                {shown.map((r) => <BkStoreCard key={r.id} r={r} />)}
                            </div>
                        </section>
                    )}
                </div>
            )}
        </div>
    );
}
